/** Shared Behandlingsforløb date/time: "man. 2. nov. 2026 · 09.00". */

const DA_WEEKDAYS = ['søn.', 'man.', 'tirs.', 'ons.', 'tors.', 'fre.', 'lør.'] as const;
const EN_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DA_MONTHS = [
  'jan.',
  'feb.',
  'mar.',
  'apr.',
  'maj',
  'jun.',
  'jul.',
  'aug.',
  'sep.',
  'okt.',
  'nov.',
  'dec.',
] as const;
const EN_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export type CourseDateParts = {
  date: Date;
  weekday: number;
  day: number;
  month: number;
  year: number;
  hours: number;
  minutes: number;
};

export function courseDateLocale(language: string): 'da' | 'en' {
  return language.toLowerCase().startsWith('en') ? 'en' : 'da';
}

export function parseCourseInstant(iso: string | null | undefined): CourseDateParts | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date,
    weekday: date.getDay(),
    day: date.getDate(),
    month: date.getMonth(),
    year: date.getFullYear(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
  };
}

export function parseCourseCivil(civil: string | null | undefined): CourseDateParts | null {
  if (!civil) return null;
  const date = new Date(`${civil}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return parseCourseInstant(date.toISOString());
}

export function formatCourseClock(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, '0')}.${String(minutes).padStart(2, '0')}`;
}

export function formatCourseTime(iso: string | null | undefined): string {
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  return formatCourseClock(parts.hours, parts.minutes);
}

export function formatCourseTimeRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string {
  const start = formatCourseTime(startIso);
  const end = formatCourseTime(endIso);
  if (!start) return '';
  return end ? `${start}–${end}` : start;
}

function weekdayLabel(weekday: number, language: string): string {
  return courseDateLocale(language) === 'en' ? EN_WEEKDAYS[weekday] : DA_WEEKDAYS[weekday];
}

function monthLabel(month: number, language: string): string {
  return courseDateLocale(language) === 'en' ? EN_MONTHS[month] : DA_MONTHS[month];
}

export function formatCourseDate(iso: string | null | undefined, language: string): string {
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  return formatCourseDateParts(parts, language);
}

export function formatCourseDateParts(parts: CourseDateParts, language: string): string {
  const weekday = weekdayLabel(parts.weekday, language);
  const month = monthLabel(parts.month, language);
  if (courseDateLocale(language) === 'en') {
    return `${weekday} ${parts.day} ${month} ${parts.year}`;
  }
  return `${weekday} ${parts.day}. ${month} ${parts.year}`;
}

/** "man. 2. nov. 2026 · 09.00" */
export function formatCourseDateTime(iso: string | null | undefined, language: string): string {
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  return `${formatCourseDateParts(parts, language)} · ${formatCourseClock(parts.hours, parts.minutes)}`;
}

/** "man. 9. nov. 2026 · 09.15–10.15" */
export function formatCourseDateTimeRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  language: string,
): string {
  const start = parseCourseInstant(startIso);
  if (!start) return '';
  const time = formatCourseTimeRange(startIso, endIso);
  return `${formatCourseDateParts(start, language)} · ${time}`;
}

export function formatCourseDayShort(iso: string | null | undefined, language: string): string {
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  const weekday = weekdayLabel(parts.weekday, language);
  const month = monthLabel(parts.month, language);
  if (courseDateLocale(language) === 'en') {
    return `${weekday} ${parts.day} ${month}`;
  }
  return `${weekday} ${parts.day}. ${month}`;
}

/** "2. nov." or the today label (e.g. "i dag"). */
export function formatCourseMiniDay(
  iso: string | null | undefined,
  language: string,
  todayLabel?: string,
  now: Date = new Date(),
): string {
  if (todayLabel && isSameCourseDay(iso, now)) {
    return todayLabel.toLocaleLowerCase(courseDateLocale(language) === 'en' ? 'en-GB' : 'da-DK');
  }
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  const month = monthLabel(parts.month, language);
  if (courseDateLocale(language) === 'en') {
    return `${parts.day} ${month}`;
  }
  return `${parts.day}. ${month}`;
}

export function formatCourseCivilDayShort(civil: string, language: string): string {
  const parts = parseCourseCivil(civil);
  if (!parts) return '';
  return formatCourseDayShort(parts.date.toISOString(), language);
}

export function isSameCourseDay(iso: string | null | undefined, now: Date = new Date()): boolean {
  const parts = parseCourseInstant(iso);
  if (!parts) return false;
  return (
    parts.year === now.getFullYear() &&
    parts.month === now.getMonth() &&
    parts.day === now.getDate()
  );
}

export function formatCourseDateTimeWithToday(
  iso: string | null | undefined,
  language: string,
  todayLabel: string,
  now: Date = new Date(),
): string {
  const formatted = formatCourseDateTime(iso, language);
  if (!formatted) return '';
  return isSameCourseDay(iso, now) ? `${todayLabel}, ${formatted}` : formatted;
}

/** "I dag, man. 9. nov. 2026 · 09.15–10.15" */
export function formatCourseDateTimeRangeWithToday(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  language: string,
  todayLabel: string,
  now: Date = new Date(),
): string {
  const formatted = formatCourseDateTimeRange(startIso, endIso, language);
  if (!formatted) return '';
  return isSameCourseDay(startIso, now) ? `${todayLabel}, ${formatted}` : formatted;
}

/** "lør. 21.–ons. 25. nov. 2026" */
export function formatCourseWindowRangeWithYear(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  language: string,
): string {
  const end = parseCourseInstant(endIso);
  const range = formatCourseWindowRange(startIso, endIso, language);
  if (!range || !end) return range;
  if (range.includes(String(end.year))) return range;
  return `${range} ${end.year}`;
}

/** "2.–23. nov. 2026" when same month/year; otherwise two full dates. */
export function formatCoursePeriod(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  language: string,
): string {
  const start = parseCourseInstant(startIso);
  const end = parseCourseInstant(endIso);
  if (!start && !end) return '';
  if (!start) return formatCourseDate(endIso, language);
  if (!end || startIso === endIso) return formatCourseDate(startIso, language);
  if (start.year === end.year && start.month === end.month) {
    const month = monthLabel(start.month, language);
    if (courseDateLocale(language) === 'en') {
      return `${start.day}–${end.day} ${month} ${start.year}`;
    }
    return `${start.day}.–${end.day}. ${month} ${start.year}`;
  }
  return `${formatCourseDate(startIso, language)}–${formatCourseDate(endIso, language)}`;
}

/** "lør. 7.–ons. 11. nov." */
export function formatCourseWindowRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  language: string,
): string {
  const start = parseCourseInstant(startIso);
  const end = parseCourseInstant(endIso);
  if (!start || !end) return formatCoursePeriod(startIso, endIso, language);
  const startWeekday = weekdayLabel(start.weekday, language);
  const endWeekday = weekdayLabel(end.weekday, language);
  const endMonth = monthLabel(end.month, language);
  if (start.year === end.year && start.month === end.month) {
    if (courseDateLocale(language) === 'en') {
      return `${startWeekday} ${start.day}–${endWeekday} ${end.day} ${endMonth}`;
    }
    return `${startWeekday} ${start.day}.–${endWeekday} ${end.day}. ${endMonth}`;
  }
  return `${formatCourseDayShort(startIso, language)}–${formatCourseDayShort(endIso, language)}`;
}

export function formatCourseWeekSpan(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  language: string,
): string {
  const start = parseCourseInstant(startIso);
  const end = parseCourseInstant(endIso);
  if (!start || !end) return formatCoursePeriod(startIso, endIso, language);
  const endMonth = monthLabel(end.month, language);
  if (start.year === end.year && start.month === end.month) {
    if (courseDateLocale(language) === 'en') {
      return `${start.day}–${end.day} ${endMonth} ${end.year}`;
    }
    return `${start.day}.–${end.day}. ${endMonth} ${end.year}`;
  }
  return `${formatCourseDayShort(startIso, language)}–${formatCourseDate(endIso, language)}`;
}
